import React, { useMemo, useState } from 'react';
import { Calendar as CalendarIcon, Clock, Users, Send } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';

const allTimeSlots = [
  '07:00', '07:30', '08:00', '08:30', '09:00', '09:30',
  '10:00', '10:30', '11:00', '11:30', '12:00', '12:30',
  '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
  '16:00', '16:30', '17:00', '17:30', '18:00', '18:30',
  '19:00', '19:30', '20:00'
];

const requestTypes = [
  { value: 'simple', label: 'Réservation simple' },
  { value: 'group', label: 'Groupe (5+ personnes)' },
  { value: 'event', label: 'Événement privé' },
  { value: 'professional', label: 'Demande professionnelle' },
  { value: 'special', label: 'Demande spéciale' }
];

export default function ReservationPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [date, setDate] = useState(undefined);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    time: '',
    guestCount: '2',
    requestType: 'simple',
    message: ''
  });

  const availableTimeSlots = useMemo(() => {
    if (!date) return allTimeSlots;

    const day = date.getDay();

    if (day >= 1 && day <= 4) {
      return allTimeSlots;
    }

    if (day === 5) {
      return allTimeSlots.filter((time) => time <= '17:00');
    }

    if (day === 6) {
      return allTimeSlots.filter((time) => time <= '14:00');
    }

    return [];
  }, [date]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateSelect = (selectedDate) => {
    setDate(selectedDate);

    if (!selectedDate) return;

    const day = selectedDate.getDay();
    let allowedSlots = allTimeSlots;

    if (day === 5) {
      allowedSlots = allTimeSlots.filter((time) => time <= '17:00');
    } else if (day === 6) {
      allowedSlots = allTimeSlots.filter((time) => time <= '14:00');
    } else if (day === 0) {
      allowedSlots = [];
    }

    if (formData.time && !allowedSlots.includes(formData.time)) {
      setFormData((prev) => ({ ...prev, time: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!date) {
      toast.error('Veuillez sélectionner une date');
      return;
    }

    if (!formData.time) {
      toast.error('Veuillez sélectionner une heure');
      return;
    }

    if (!availableTimeSlots.includes(formData.time)) {
      toast.error('Cette heure n’est pas disponible pour la date sélectionnée');
      return;
    }

    setIsLoading(true);

    try {
      const guestCount =
        formData.guestCount === '10+'
          ? 10
          : parseInt(formData.guestCount, 10);

      if (Number.isNaN(guestCount) || guestCount < 1) {
        throw new Error('Nombre de personnes invalide');
      }

      const reservationPayload = {
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        reservation_date: format(date, 'yyyy-MM-dd'),
        reservation_time: formData.time,
        guest_count: guestCount,
        request_type: formData.requestType || 'simple',
        special_request: formData.message.trim() || null,
        status: 'pending',
      };

      console.log('Submitting reservation payload:', reservationPayload);

      const { data, error } = await supabase.functions.invoke('create-reservation', {
        body: reservationPayload,
      });

      console.log('Function response:', { data, error });

      if (error) {
        throw error;
      }

      if (data?.success === false) {
        throw new Error(data.message || 'Erreur lors de la création de la réservation');
      }

      toast.success('Demande envoyée !', {
        description: 'Nous vous confirmerons votre réservation par email.',
      });

      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        time: '',
        guestCount: '2',
        requestType: 'simple',
        message: '',
      });
      setDate(undefined);
    } catch (error) {
      console.error('Reservation error object:', error);
      console.error('Reservation error name:', error?.name);
      console.error('Reservation error message:', error?.message);
      console.error('Reservation error context:', error?.context);

      const message =
        error?.message && error.message !== 'Edge Function returned a non-2xx status code'
          ? error.message
          : 'Une erreur est survenue. Veuillez réessayer.';

      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const pageTitle = "Réservation | Léger's Coffee Genève";
  const pageDescription =
    "Réservez votre table chez Léger's Coffee à Genève pour un café, un brunch, un rendez-vous ou un événement privé. Demande de réservation simple, groupe ou événement spécial.";
  const canonicalUrl = 'https://legerscoffee.fr/reservation';
  const ogImage = 'https://legerscoffee.fr/og-image.jpg';

  const structuredDataReservationPage = {
    '@context': 'https://schema.org',
    '@type': 'ReservationPage',
    name: "Réservation | Léger's Coffee",
    url: canonicalUrl,
    description: pageDescription,
    inLanguage: 'fr-CH'
  };

  const structuredDataBusiness = {
    '@context': 'https://schema.org',
    '@type': 'CafeOrCoffeeShop',
    name: "Léger's Coffee",
    url: 'https://legerscoffee.fr/',
    image: [ogImage],
    acceptsReservations: 'True',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Pl. des Augustins 19',
      postalCode: '1205',
      addressLocality: 'Genève',
      addressCountry: 'CH'
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday'],
        opens: '07:00',
        closes: '20:30'
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Friday',
        opens: '07:00',
        closes: '17:00'
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Saturday',
        opens: '07:00',
        closes: '14:00'
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Léger's Coffee" />
        <meta property="og:locale" content="fr_CH" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImage} />

        <script type="application/ld+json">
          {JSON.stringify(structuredDataReservationPage)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(structuredDataBusiness)}
        </script>
      </Helmet>

      <main data-testid="reservation-page" className="min-h-screen pt-20">
        <section className="bg-warm-secondary py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center max-w-2xl mx-auto">
              <span className="text-latte-gold text-sm uppercase tracking-widest font-medium mb-4 block">
                Réservation
              </span>
              <h1 className="font-serif text-4xl lg:text-5xl text-espresso font-semibold mb-6">
                Réservez Votre Table
              </h1>
              <p className="text-soft-charcoal-secondary">
                Planifiez votre visite chez Léger&apos;s Coffee. Pour les groupes ou événements spéciaux,
                nous vous répondrons dans les 24 heures.
              </p>
            </div>
          </div>
        </section>

        <section className="section-padding bg-warm-alabaster">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white p-8 lg:p-12 border border-border-subtle">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <h2 className="font-serif text-xl text-espresso font-semibold mb-4">
                    Vos Informations
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">Prénom *</Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        className="input-field"
                        data-testid="reservation-firstname"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Nom *</Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        className="input-field"
                        data-testid="reservation-lastname"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="input-field"
                        data-testid="reservation-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Téléphone *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="input-field"
                        data-testid="reservation-phone"
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-border-subtle">
                  <h2 className="font-serif text-xl text-espresso font-semibold mb-4">
                    Détails de la Réservation
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date souhaitée *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full h-12 justify-start text-left font-normal border-border-subtle rounded-none"
                            data-testid="reservation-date"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 text-latte-gold" />
                            {date ? (
                              format(date, 'PPP', { locale: fr })
                            ) : (
                              <span className="text-soft-charcoal-secondary">Sélectionner une date</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={date}
                            onSelect={handleDateSelect}
                            disabled={(date) => {
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              const day = date.getDay();
                              return date < today || day === 0;
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="space-y-2">
                      <Label>Heure souhaitée *</Label>
                      <Select
                        value={formData.time}
                        onValueChange={(value) => setFormData((prev) => ({ ...prev, time: value }))}
                      >
                        <SelectTrigger
                          className="h-12 rounded-none border-border-subtle"
                          data-testid="reservation-time"
                        >
                          <Clock className="mr-2 h-4 w-4 text-latte-gold" />
                          <SelectValue placeholder="Sélectionner une heure" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableTimeSlots.map((time) => (
                            <SelectItem key={time} value={time}>
                              {time}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Nombre de personnes *</Label>
                      <Select
                        value={formData.guestCount}
                        onValueChange={(value) => setFormData((prev) => ({ ...prev, guestCount: value }))}
                      >
                        <SelectTrigger
                          className="h-12 rounded-none border-border-subtle"
                          data-testid="reservation-guests"
                        >
                          <Users className="mr-2 h-4 w-4 text-latte-gold" />
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                            <SelectItem key={num} value={num.toString()}>
                              {num} {num === 1 ? 'personne' : 'personnes'}
                            </SelectItem>
                          ))}
                          <SelectItem value="10+">Plus de 10 personnes</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Type de demande</Label>
                      <Select
                        value={formData.requestType}
                        onValueChange={(value) => setFormData((prev) => ({ ...prev, requestType: value }))}
                      >
                        <SelectTrigger
                          className="h-12 rounded-none border-border-subtle"
                          data-testid="reservation-type"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {requestTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2 mt-4">
                    <Label htmlFor="message">Message (optionnel)</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      className="border-border-subtle focus:border-espresso rounded-none resize-none"
                      placeholder="Informations complémentaires, demandes spéciales..."
                      data-testid="reservation-message"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full btn-primary"
                  data-testid="reservation-submit"
                >
                  {isLoading ? (
                    'Envoi en cours...'
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Envoyer la Demande
                    </>
                  )}
                </Button>

                <p className="text-xs text-soft-charcoal-secondary text-center">
                  Nous vous confirmerons votre réservation par email dans les 24 heures.
                </p>
              </form>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}